import { supabase } from '@/lib/supabase';
import { getStudyScope, isStudyScopeCurrent, readGenerationSession, writeGenerationSession } from './studyStorage';
import type { LessonCanvasDocument, LessonVisualBeat } from './lessonTypes';
import type { ServerGenerationAccess } from './generationAccess';
import { generationPollDelayMs, generationRequestBody } from './generation-polling';
export type { LessonElementType, LessonDiagramKind, LessonDiagramData, LessonCanvasElement, LessonVisualBeat, LessonCanvasDocument } from './lessonTypes';

export interface AIAnalysisResult {
  generation?: ServerGenerationAccess;
  durationNotice?: string;
  progressivePlaybackFallback?: boolean;
  generationMetrics?: {
    progressiveDelivery?: 'progressive' | 'full_audio_fallback' | 'not_used';
    progressiveFallbackReason?: 'private_storage' | 'browser_playback' | 'no_progressive_sections';
    tier: 'FREE' | 'PREMIUM';
    planDurationLimitSeconds: number;
    requestedDurationSeconds: number | null;
    estimatedNarrationDurationSeconds: number | null;
    actualNarrationDurationSeconds: number | null;
    narrationCharacters: number;
    durationRepairAttempts: number;
    openAIImagesGenerated: number;
    openAIImageUsage: {
      inputTokens: number;
      outputTokens: number;
      totalTokens: number;
    } | null;
    openAIUsage: {
      inputTokens?: number;
      outputTokens?: number;
      totalTokens?: number;
    };
  };
  id: string;
  subject: string;
  topic: string;
  title: string;
  scannedImage: string;
  cleanQuestion?: string;
  extractedText: string;
  detectedQuestions: Array<{
    number: number;
    text: string;
    questionType: string;
    whatIsBeingAsked: string;
    keyTerms: string[];
  }>;
  overallSubject: {
    name: string;
    confidence: 'high' | 'medium' | 'low';
    topicsDetected: string[];
  };
  detectedEquations: string[];
  suggestedNextSteps: string[];
  explanation: {
    summary: string;
    steps: Array<{
      number: number;
      title: string;
      content: string[];
      result: string;
      explanation?: string;
      calculation?: string;
      noteType?: 'important' | 'note' | 'tip' | 'remember' | 'watch-out' | 'quick-check';
      note?: string;
      visualActions?: Array<{
        narration: string;
        show: string;
        focus?: string;
        highlightStyle?: 'marker' | 'underline' | 'circle';
        teacherNote?: string;
        kind?: 'handwriting' | 'equation' | 'annotation' | 'result';
        inkColor?: 'normal' | 'concept' | 'warning' | 'result' | 'emphasis';
      }>;
    }>;
    followUpQuestions: string[];
  };
  lessonPlan: {
    chapters: Array<{
      id: string;
      label: string;
      script: string;
      startSeconds: number;
      durationSeconds: number;
    }>;
  };
  lessonCanvas: LessonCanvasDocument;
  narration: {
    provider: 'elevenlabs' | 'browser';
    audioUrl?: string;
    guestAccessToken?: string;
    warning?: string;
  };
}

export interface AIChatRequest {
  messages: Array<{ role: 'user' | 'assistant'; content: string }>;
  preferences?: {
    tutorMode?: string;
    learningStyle?: string;
    explanationStyle?: string;
    learningMode?: string;
  };
  context?: {
    title?: string;
    subject?: string;
    topic?: string;
    summary?: string;
    keyPoints?: string[];
    narration?: string;
  };
}

async function getAIRequestHeaders() {
  const owner = getStudyScope();
  const { data } = await supabase.auth.getSession();
  const accessToken = data.session?.access_token;
  if (!isStudyScopeCurrent(owner) || (data.session?.user.id || null) !== owner.ownerId) {
    throw new Error('Your study session changed. Please send your question again.');
  }
  return {
    'Content-Type': 'application/json',
    ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    ...(!accessToken && !owner.ownerId && owner.guestId ? { 'X-Lavyn-Guest-Session': owner.guestId } : {}),
  };
}

export type GenerationHistoryResume =
  | {
      status: 'incomplete';
      generationId: string;
      requestId: string;
      completedSegmentCount: number;
      resumeInput: {
        mode: 'topic' | 'scan';
        subject: string;
        grade: string;
        topic: string;
        difficulty: string;
        image: string;
        presentationReference: string;
        instruction: string;
        teachingMode: 'answer' | 'explain';
        learningMode?: 'effective' | 'step-by-step';
        outputFormat: 'video';
      };
    }
  | { status: 'completed'; generationId: string; result: AIAnalysisResult };

export async function fetchGenerationHistoryResume(generationId: string): Promise<GenerationHistoryResume> {
  if (!/^ai-[a-zA-Z0-9_-]{1,100}$/.test(generationId)) {
    throw new Error('This History item does not have a valid generation ID.');
  }
  const headers = await getAIRequestHeaders();
  const response = await fetch(`/api/ai/generation-resume?generationId=${encodeURIComponent(generationId)}`, { headers });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw Object.assign(new Error(typeof payload.error === 'string'
      ? payload.error : 'This saved lesson could not be resumed.'), {
      code: typeof payload.code === 'string' ? payload.code : 'GENERATION_RESUME_UNAVAILABLE',
    });
  }
  if (payload.status === 'completed' && payload.result && typeof payload.result === 'object') {
    return payload as GenerationHistoryResume;
  }
  if (payload.status !== 'incomplete' || payload.generationId !== generationId ||
      payload.requestId !== generationId.slice(3) || !payload.resumeInput || typeof payload.resumeInput !== 'object') {
    throw new Error('The saved generation recovery record is incomplete or invalid.');
  }
  return payload as GenerationHistoryResume;
}

export async function sendAIChatMessage(input: AIChatRequest): Promise<string> {
  const headers = await getAIRequestHeaders();
  const response = await fetch('/api/ai/chat', {
    method: 'POST',
    headers,
    body: JSON.stringify(input),
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload.error || 'The tutor could not answer this question.');
  }
  if (typeof payload.answer !== 'string' || !payload.answer.trim()) {
    throw new Error('The tutor returned an empty response. Please try again.');
  }
  return payload.answer.trim();
}

export async function generateAIContent(input: {
  requestId?: string;
  mode: 'topic' | 'scan';
  subject?: string;
  grade?: string;
  topic?: string;
  difficulty?: string;
  image?: string;
  instruction?: string;
  teachingMode?: 'answer' | 'explain';
  learningMode?: 'effective' | 'step-by-step';
  outputFormat?: 'text' | 'video';
  presentationReference?: string;
}, onGenerationStatus?: (access: ServerGenerationAccess, queue?: { status: string; position: number }) => void) {
  const owner = getStudyScope();
  const pollingStartedAt = Date.now();
  let clientGenerationStartedAt = pollingStartedAt;
  let apiRoundTripMs = 0;
  let pollingWaitMs = 0;
  let pollRequests = 0;
  let lastError: Error | null = null;
  const headers = await getAIRequestHeaders();
  if (!isStudyScopeCurrent(owner)) throw new Error('Your account changed before generation started. Please reopen the question in the original account.');
  let providerResponseId = '';
  let resumeToken = '';
  let usageNotified = false;
  const generationStorageKey = 'lavyn_generation_job';
  if (typeof window !== 'undefined' && input.requestId) {
    try {
      const stored = JSON.parse(readGenerationSession(generationStorageKey, owner) || '{}') as {
        requestId?: string;
        providerResponseId?: string;
        resumeToken?: string;
        startedAt?: number;
      };
      if (stored.requestId === input.requestId) {
        providerResponseId = stored.providerResponseId || '';
        resumeToken = stored.resumeToken || '';
        if (Number.isFinite(stored.startedAt) && stored.startedAt! > 0) {
          clientGenerationStartedAt = stored.startedAt!;
        }
      }
    } catch {
      // The analyzing screen will recreate malformed resume state.
    }
  }

  // Reasoning, scientific illustrations, and narration can take several minutes.
  // Background Responses may remain queued well beyond a normal HTTP request.
  // Polling the saved response ID is read-only; do not create a replacement job.
  const maxAttempts = 150;
  const deadline = Date.now() + 60 * 60 * 1000;
  for (let attempt = 0; attempt < maxAttempts && Date.now() < deadline; attempt++) {
    const requestStartedAt = Date.now();
    let requestTimingRecorded = false;
    try {
      const response = await fetch('/api/ai/analyze', {
        method: 'POST',
        headers,
        body: JSON.stringify(generationRequestBody(input, providerResponseId, resumeToken)),
      });
      const payload = await response.json().catch(() => ({})) as Record<string, unknown>;
      if (!requestTimingRecorded) {
        apiRoundTripMs += Date.now() - requestStartedAt;
        pollRequests++;
        requestTimingRecorded = true;
      }
      if (payload.generation) {
        onGenerationStatus?.(payload.generation as ServerGenerationAccess, payload.queue as { status: string; position: number } | undefined);
        if (typeof window !== 'undefined' && (!usageNotified || response.status !== 202)) {
          usageNotified = true;
          window.dispatchEvent(new Event('lavyn:generation-usage-changed'));
        }
      }

      if (response.ok && response.status !== 202) {
        if (input.outputFormat === 'video' && typeof console !== 'undefined') {
          console.info('[LAVYN generation timing]', JSON.stringify({
            endToEndMs: Math.max(0, Date.now() - clientGenerationStartedAt),
            apiRoundTripMs,
            pollingWaitMs,
            pollRequests,
            resumed: Boolean(providerResponseId),
          }));
        }
        return payload as unknown as AIAnalysisResult;
      }
      if (response.status === 202) {
        providerResponseId = typeof payload.providerResponseId === 'string'
          ? payload.providerResponseId
          : providerResponseId;
        resumeToken = typeof payload.resumeToken === 'string' ? payload.resumeToken : resumeToken;
        if (typeof window !== 'undefined' && input.requestId &&
            (input.outputFormat === 'video' || (providerResponseId && resumeToken))) {
          try {
            const current = JSON.parse(readGenerationSession(generationStorageKey, owner) || '{}');
            writeGenerationSession(generationStorageKey, JSON.stringify({
              ...current,
              requestId: input.requestId,
              ...(providerResponseId ? { providerResponseId } : {}),
              ...(resumeToken ? { resumeToken } : {}),
              startedAt: clientGenerationStartedAt,
            }), owner);
          } catch {
            // Generation still continues in this tab even if storage is unavailable.
          }
        }
      }
      const isRetryable = response.status === 202 ||
        (attempt < 3 && payload.retryable !== false && [429, 502, 503, 504].includes(response.status));
      if (!isRetryable) {
        // A failed model or invalid input must not become repeated paid jobs.
        throw Object.assign(new Error(typeof payload.error === 'string' ? payload.error : 'The AI could not complete this request.'), {
          terminal: true,
          retryWithNewRequestId: payload.retryWithNewRequestId === true,
        });
      }
      lastError = new Error(typeof payload.error === 'string' ? payload.error : 'The lesson is still being generated.');
    } catch (error) {
      if (!requestTimingRecorded) {
        apiRoundTripMs += Date.now() - requestStartedAt;
        pollRequests++;
      }
      lastError = error instanceof Error ? error : new Error('The AI could not complete this request.');
      if ((error as { terminal?: boolean })?.terminal || attempt === maxAttempts - 1) throw lastError;
    }

    const elapsedMs = Date.now() - pollingStartedAt;
    const delayMs = generationPollDelayMs(attempt, elapsedMs);
    pollingWaitMs += delayMs;
    await new Promise((resolve) => setTimeout(resolve, delayMs));
  }

  throw lastError || new Error('The AI could not complete this request.');
}
